/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { StandardTermsStatement } from '../services/standard-terms.service';
import { configService, baselineService, standardTermsService } from '../services';
import type { SystemDefaults, BaselineConfig } from '../services';
import { DEMAND_SOURCES_KEY } from './useDemandSources';

export const CONFIG_KEY = 'system-defaults';
export const BASELINE_CONFIG_KEY = 'baseline-config';

export function useSystemDefaults() {
  return useQuery({
    queryKey: [CONFIG_KEY],
    queryFn: () => configService.getSystemDefaults(),
  });
}

export function useSaveSystemDefaults() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (defaults: SystemDefaults) => configService.saveSystemDefaults(defaults),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CONFIG_KEY] }),
  });
}

export function useBaselineConfig() {
  return useQuery({
    queryKey: [BASELINE_CONFIG_KEY],
    queryFn: () => baselineService.getBaselineConfig(),
  });
}

export function useSaveBaselineConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (config: BaselineConfig) => baselineService.saveBaselineConfig(config),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [BASELINE_CONFIG_KEY] }),
  });
}

export const STANDARD_TERMS_KEY = 'standard-terms';

/** Standard Terms (`3c-ii`, `#1398`): the base tier a blank member of every source's Terms inherits. */
export function useStandardTerms() {
  return useQuery({
    queryKey: [STANDARD_TERMS_KEY],
    queryFn: () => standardTermsService.getStandardTerms(),
  });
}

/**
 * Save Standard Terms. **Invalidates every demand source too**: a source that states nothing takes
 * these values, so its resolved terms — which every source read carries — are now different.
 */
export function useSaveStandardTerms() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (statement: StandardTermsStatement) => standardTermsService.saveStandardTerms(statement),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [STANDARD_TERMS_KEY] });
      void queryClient.invalidateQueries({ queryKey: [DEMAND_SOURCES_KEY] });
    },
  });
}
